<template>
  <div>
    <v-container class="py-2">
      <v-row class="mt-6">
        <v-col md="10" offset-md="1" lg="8" offset-lg="2">
          <div v-if="photo.statusName !== types.photo.status.inCuration">
            <h3 class="text-h5 font-weight-bold mb-6">
              Add a comment
            </h3>
            <v-form
              ref="form"
              v-model="valid"
              novalidate
              lazy-validation
              @submit.prevent="noop"
            >
              <!-- Comment textarea -->
              <v-textarea
                v-model="newComment"
                :rules="commentRules"
                validate-on-blur
                hint="English is the preferred language. You can still post comments in your own language and we will automatically translate it for you!"
                auto-grow
                outlined
                counter
                rows="1"
                @paste="onPaste"
                @focus="setAllowKeyboardNavigation(false)"
                @blur="setAllowKeyboardNavigation(true)"
              >
                <template #progress>
                  <v-progress-linear
                    :value="progress"
                    height="3"
                    class="mt-1"
                    absolute
                  ></v-progress-linear>
                </template>
              </v-textarea>
              <div class="text-right">
                <v-btn
                  :loading="processing"
                  type="submit"
                  dark
                  depressed
                  @click="addComment"
                >
                  <v-icon small class="mr-2">fal fa-check</v-icon>
                  Submit
                </v-btn>
              </div>
            </v-form>
          </div>
          <div v-if="comments.length > 0 && !loading">
            <h3 class="text-h5 font-weight-bold mb-6">
              {{ photo.numberOfComments || 0 }} Comment<span
                v-if="comments.length > 1"
                >s</span
              >:
            </h3>
            <div class="comments-wrapper">
              <div
                v-for="(c, i) in comments"
                :key="i"
                class="d-flex comment-wrapper"
              >
                <div class="avatar-wrapper mr-6">
                  <nuxt-link
                    :to="`/photographer/${c.authorUserName || c.authorId}`"
                  >
                    <div>
                      <v-avatar size="42px">
                        <img
                          v-if="c.authorAvatarUrl"
                          :src="c.authorAvatarUrl"
                          :alt="`${c.authorName} avatar.`"
                        />
                      </v-avatar>
                      <div
                        v-if="c.authorHasAnActiveSubscription && c.authorSubscriptionType !== types.subscription.type.basic"
                        class="text-center"
                      >
                        <span
                          :class="
                            $vuetify.theme.dark
                              ? 'black--text'
                              : 'secondary--text'
                          "
                          class="pro-badge primary mt-2 font-weight-bold text-caption"
                        >
                          <small>
                            {{
                              c.authorSubscriptionType ===
                              types.subscription.type.pro
                                ? 'PRO'
                                : 'PRO +'
                            }}
                          </small>
                        </span>
                      </div>
                    </div>
                  </nuxt-link>
                </div>
                <div class="w-100-percentage">
                  <div class="content-wrapper">
                    <h4>
                      <nuxt-link
                        :to="`/photographer/${c.authorUserName || c.authorId}`"
                        :class="{
                          'secondary--text': dark || $vuetify.theme.dark
                        }"
                        class="text-plain"
                        >{{ c.authorName }}</nuxt-link
                      >
                      <span class="text-caption badge ml-2">
                        <span
                          v-if="
                            c.authorRole === types.user.role.curator ||
                              c.authorRole === types.user.role.ambassador
                          "
                        >
                          <img
                            :src="
                              `/images/100asa-round-icon${
                                dark || $vuetify.theme.dark ? '-white' : ''
                              }.png`
                            "
                            alt="100ASA icon."
                            width="14"
                            height="14"
                          />
                        </span>
                        <span v-else>
                          <i class="fal fa-trophy"></i>
                          {{ c.authorReputation }} &#8226; {{ c.authorRole }}
                        </span>
                      </span>
                    </h4>
                    <h6 class="text-caption mb-2">
                      Curated {{ c.postDate | timeago }}
                    </h6>
                    <p class="ma-0 mb-4">
                      <v-textarea
                        v-if="isEditingComment && me && me.id === c.authorId"
                        id="comment"
                        v-model="comment"
                        :rules="commentRules"
                        :loading="true"
                        validate-on-blur
                        auto-grow
                        outlined
                        counter
                        rows="3"
                        label="Edit your comment"
                        @paste="onPaste"
                        @focus="setAllowKeyboardNavigation(false)"
                        @blur="setAllowKeyboardNavigation(true)"
                      >
                        <template #progress>
                          <v-progress-linear
                            :value="progress"
                            height="3"
                            class="mt-2"
                            absolute
                          ></v-progress-linear>
                        </template>
                      </v-textarea>
                      <span v-else>{{ c.body }}</span>
                    </p>
                    <div v-if="c.language && c.language !== 'en'">
                      <p class="text-caption font-weight-bold mt-4 mb-2">
                        <v-icon small class="mr-2">fal fa-language</v-icon
                        >Comment automatically translated to English:
                      </p>
                      <p class="mb-4">{{ c.translatedBody }}</p>
                    </div>
                    <div class="actions mb-2">
                      <template v-if="me && me.isAdmin">
                        <v-tooltip top>
                          <template #activator="{ on }">
                            <v-btn
                              small
                              outlined
                              class="text-caption bluezone--text mb-2 ml-0 mr-2"
                              v-on="on"
                              @click="removeComment(c)"
                            >
                              <v-icon x-small class="bluezone--text"
                                >fal fa-trash-alt</v-icon
                              >
                            </v-btn>
                          </template>
                          <span>Remove comment</span>
                        </v-tooltip>
                      </template>
                      <template
                        v-if="me && me.id === c.authorId && !isEditingComment"
                      >
                        <v-tooltip top>
                          <template #activator="{ on }">
                            <v-btn
                              class="text-caption ml-0 mb-2 mr-2"
                              small
                              outlined
                              v-on="on"
                              @click="editComment(c)"
                            >
                              <v-icon x-small>fal fa-edit</v-icon>
                            </v-btn>
                          </template>
                          <span>Edit your comment</span>
                        </v-tooltip>
                      </template>
                      <v-tooltip top>
                        <template #activator="{ on }">
                          <v-btn
                            :class="{
                              primary:
                                c.lastActionOnComment === 1 &&
                                !$vuetify.theme.dark,
                              secondary:
                                c.lastActionOnComment === 1 &&
                                $vuetify.theme.dark
                            }"
                            :outlined="c.lastActionOnComment !== 1"
                            :depressed="c.lastActionOnComment === 1"
                            :loading="loadingLikeComment === c.id"
                            :disabled="c.lastActionOnComment === -1"
                            small
                            class="text-caption mb-2 ml-0 mr-2"
                            v-on="on"
                            @click="likeComment(c.id, c.authorId)"
                          >
                            <v-icon x-small class="mr-2"
                              >fal fa-thumbs-up</v-icon
                            >
                            {{ c.numberOfLikes || 0 }}
                          </v-btn>
                        </template>
                        <span>Like comment</span>
                      </v-tooltip>
                      <v-tooltip top>
                        <template #activator="{ on }">
                          <v-btn
                            :class="{
                              primary:
                                c.lastActionOnComment === -1 &&
                                !$vuetify.theme.dark,
                              secondary:
                                c.lastActionOnComment === -1 &&
                                $vuetify.theme.dark
                            }"
                            :outlined="c.lastActionOnComment !== -1"
                            :depressed="c.lastActionOnComment === -1"
                            :loading="loadingDislikeComment === c.id"
                            :disabled="c.lastActionOnComment === 1"
                            small
                            class="text-caption mb-2 ml-0 mr-2"
                            v-on="on"
                            @click="dislikeComment(c.id, c.authorId)"
                          >
                            <v-icon x-small class="mr-2"
                              >fal fa-thumbs-down</v-icon
                            >
                            {{ c.numberOfDislikes || 0 }}
                          </v-btn>
                        </template>
                        <span>Dislike comment</span>
                      </v-tooltip>
                      <Discussion
                        :comment="c"
                        :photo="photo"
                        :me="me"
                        :is-editing-comment="isEditingComment"
                      />
                      <template v-if="!me || (me && me.id !== c.authorId)">
                        <v-tooltip top>
                          <template #activator="{ on }">
                            <v-btn
                              small
                              outlined
                              class="text-caption mb-2 ml-0 mr-2"
                              color="error"
                              v-on="on"
                              @click="reportComment(c)"
                            >
                              <v-icon x-small>fal fa-flag</v-icon>
                            </v-btn>
                          </template>
                          <span>Report comment</span>
                        </v-tooltip>
                      </template>
                      <v-btn
                        v-if="me && me.id === c.authorId && isEditingComment"
                        class="text-caption ml-0 mb-2 mr-2"
                        small
                        outlined
                        @click="cancelEditComment()"
                      >
                        <v-icon x-small class="mr-2">fal fa-times</v-icon>
                        Cancel
                      </v-btn>
                      <v-btn
                        v-if="
                          me &&
                            me.id === c.authorId &&
                            isEditingComment &&
                            c.body !== comment
                        "
                        class="text-caption ml-0 mb-2 mr-2"
                        small
                        outlined
                        @click="submitEditComment(c.id)"
                      >
                        <v-icon x-small class="mr-2">fal fa-check</v-icon>
                        Save
                      </v-btn>
                    </div>
                  </div>
                  <v-divider
                    v-if="i < comments.length - 1"
                    class="mt-3 mb-6"
                  ></v-divider>
                </div>
              </div>
            </div>
          </div>
          <div
            v-else-if="comments.length === 0 && !loading"
            class="text-center"
          >
            <h3 class="text-h6 font-weight-bold mb-6">
              <v-icon medium class="mr-2">fas fa-frown</v-icon>
              This photo hasn't received any comments yet...
            </h3>
          </div>
          <div v-intersect="onIntersect" />
          <div
            v-if="loading || loadingMore"
            class="w-100-percentage text-center"
          >
            <v-icon class="fa-spin">fal fa-spinner</v-icon>
          </div>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
import moment from 'moment'
import types from '@/utils/types'
import { displayConfirm } from '@/utils/swal'
import { EventBus } from '@/plugins/event-bus'

export default {
  name: 'PhotoComments',
  components: {
    Discussion: () => import('@/components/photo/Discussion.vue')
  },
  filters: {
    timeago: date => {
      return moment
        .utc(date)
        .local()
        .fromNow()
    }
  },
  props: {
    me: {
      type: [Object, Boolean],
      default: () => {}
    },
    photo: {
      type: Object,
      default: () => {}
    },
    comments: {
      type: Array,
      default: () => []
    },
    isLastPage: {
      type: Boolean,
      default: false
    },
    page: {
      type: Number,
      default: 1
    },
    loading: {
      type: Boolean,
      default: false
    },
    dark: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      mounted: false,
      types,
      valid: false,
      comment: '',
      newComment: '',
      commentRules: [
        v =>
          v.length >= 60 ||
          'Your comment should be at least 60 characters length',
        v =>
          v.length < 4000 ||
          'Your comment is too long, maximum characters length allowed is 4000'
      ],
      processing: false,
      isEditingComment: false,
      loadingMore: false,
      loadingLikeComment: null,
      loadingDislikeComment: null
    }
  },
  computed: {
    progress() {
      return Math.min(120, this.comment.length * 0.8333)
    }
  },
  methods: {
    noop() {},
    onIntersect(entries) {
      if (
        entries[0].intersectionRatio > 0 &&
        !this.isLastPage &&
        !this.loadingMore &&
        !this.loading &&
        this.comments.length > 0
      ) {
        this.loadMoreComments()
      }
    },
    setAllowKeyboardNavigation(value) {
      EventBus.$emit('set-allow-keyboard-navigation', value)
    },
    async loadMoreComments() {
      this.loadingMore = true
      try {
        await this.$store.dispatch('comments/getPhotoComments', {
          page: this.page + 1,
          id: this.$route.params.id
        })
      } catch (error) {
        this.$asa.appInsights('Comp: photo/Comments.vue - method: loadMoreComments', error)
      } finally {
        this.loadingMore = false
      }
    },
    editComment(comment) {
      this.comment = comment.body
      this.isEditingComment = true
    },
    cancelEditComment() {
      this.comment = ''
      this.isEditingComment = false
    },
    removeComment(comment) {
      displayConfirm(
        'By clicking continue you will delete this comment and this action cannot be undone!',
        async result => {
          if (result.isConfirmed) {
            try {
              await this.$store.dispatch('comments/removeComment', comment.id)
              EventBus.$emit('snackbar', {
                text: 'The comment has been successfully removed',
                color: 'success'
              })
            } catch (error) {
              this.$asa.appInsights('Comp: photo/Comments.vue - method: removeComment', error)
            }
          }
        }
      )
    },
    reportComment(comment) {
      if (this.me && this.me.id !== comment.authorId) {
        displayConfirm(
          'You are reporting this comment because it contains offensive words or phrases. Click continue to submit your request.',
          async result => {
            if (result.isConfirmed) {
              try {
                await this.$store.dispatch('comments/reportComment', comment.id)
                EventBus.$emit('snackbar', {
                  text: 'The comment has been successfully reported',
                  color: 'success'
                })
              } catch (error) {
                this.$asa.appInsights('Comp: photo/Comments.vue - method: reportComment', error)
              }
            }
          }
        )
      }
      if (!this.me) {
        EventBus.$emit('snackbar', {
          text: 'You need to log in to report this comment',
          color: 'info'
        })
      }
    },
    async submitEditComment(id) {
      const data = {
        id,
        body: this.comment
      }
      try {
        await this.$store.dispatch('comments/patchComment', data)
        this.cancelEditComment()
      } catch (error) {
        this.$asa.appInsights('Comp: photo/Comments.vue - method: submitEditComment', error)
      }
    },
    async likeComment(id, authorId) {
      if (!this.me) {
        EventBus.$emit('snackbar', {
          text: 'You need to log in to perform this action',
          color: 'info'
        })
      } else if (this.me && this.me.id === authorId) {
        EventBus.$emit('snackbar', {
          text: 'You cannot like your own comments',
          color: 'info'
        })
      } else {
        this.loadingLikeComment = id
        try {
          await this.$store.dispatch('comments/postLike', id)
          await this.$auth.fetchUser()
        } catch (error) {
          this.$asa.appInsights('Comp: photo/Comments.vue - method: likeComment', error)
        } finally {
          this.loadingLikeComment = null
        }
      }
    },
    async dislikeComment(id, authorId) {
      if (!this.me) {
        EventBus.$emit('snackbar', {
          text: 'You need to log in to perform this action',
          color: 'info'
        })
      } else if (this.me && this.me.id === authorId) {
        EventBus.$emit('snackbar', {
          text: 'You cannot dislike your own comments',
          color: 'info'
        })
      } else {
        this.loadingDislikeComment = id
        try {
          await this.$store.dispatch('comments/postDislike', id)
          await this.$auth.fetchUser()
        } catch (error) {
          this.$asa.appInsights('Comp: photo/Comments.vue - method: dislikeComment', error)
        } finally {
          this.loadingDislikeComment = null
        }
      }
    },
    async addComment() {
      if (!this.me) {
        EventBus.$emit('snackbar', {
          text: 'You need to be logged in to add a comment to this photo',
          color: 'info'
        })
        return
      }
      if (this.$refs.form.validate()) {
        const data = {
          photoId: this.photo.id,
          user: this.me.id,
          body: this.newComment.trim()
        }
        try {
          this.processing = true
          await this.$store.dispatch('comments/postComment', data)
          this.newComment = ''
          EventBus.$emit('snackbar', {
            text: 'Your comment has been submitted successfully',
            color: 'success'
          })
        } catch (error) {
          this.$asa.appInsights('Comp: photo/Comments.vue - method: addComment', error)
        } finally {
          this.processing = false
        }
      } else {
        EventBus.$emit('snackbar', {
          text: 'Please enter a valid comment',
          color: 'error'
        })
      }
    },
    onPaste(event) {
      const canPaste =
        this.me && (this.me.isAdmin || this.me.role === types.user.role.curator)
      if (!canPaste) {
        event.preventDefault()
        EventBus.$emit('snackbar', {
          text: 'Copy and paste function is disabled for comments.',
          color: 'error',
          timeout: 0
        })
      }
    }
  }
}
</script>

<style scoped>
.content-wrapper {
  flex-grow: 1;
  width: 100%;
}
.text-plain {
  text-decoration: none;
}
.column {
  align-items: center;
  flex-direction: column;
}
.badge {
  background-color: rgba(0, 0, 0, 0.12);
  border-radius: 12px;
  padding: 3px 10px;
}
.badge img {
  vertical-align: text-top;
}
.avatar-wrapper {
  flex: 0 !important;
  width: 42px;
}
.avatar-wrapper img {
  width: 42px;
  height: 42px;
}
.pro-badge {
  border-radius: 4px;
  display: block;
  width: 42px;
}
</style>
